import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiMap, FiMapPin, FiPlus, FiFilter, FiList } from 'react-icons/fi';

import { Spinner } from '../../components/ui/Spinner';
import { Alert } from '../../components/ui/Alert';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { MapaZeladoria } from '../../components/MapaZeladoria';
import { supabase } from '../../lib/supabase';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';
import {
  zeladoriaPortalService, TIPO_ZELADORIA_LABEL, TIPOS_ZELADORIA_LISTA,
  TIPO_ZELADORIA_COR, type TipoZeladoria, type PontoMapaZeladoria,
} from '../../services/zeladoria.service';

export function ZeladoriaMapaPage() {
  const [estab, setEstab] = useState<{ nome: string; cor_primaria: string | null } | null>(null);
  const [pinos, setPinos] = useState<PontoMapaZeladoria[]>([]);
  const [filtroTipo, setFiltroTipo] = useState<TipoZeladoria | ''>('');
  const [apenasAbertas, setApenasAbertas] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    const id = getEstabelecimentoAtualId();
    supabase.from('estabelecimentos').select('nome, cor_primaria').eq('id', id).maybeSingle()
      .then(({ data }) => { if (data) setEstab(data as { nome: string; cor_primaria: string | null }); });
  }, []);

  useEffect(() => {
    setCarregando(true);
    setErro(null);
    const id = getEstabelecimentoAtualId();
    zeladoriaPortalService.mapa(id, filtroTipo || undefined, apenasAbertas)
      .then(setPinos)
      .catch((e) => setErro(e instanceof Error ? e.message : 'Erro ao carregar mapa'))
      .finally(() => setCarregando(false));
  }, [filtroTipo, apenasAbertas]);

  // Contagem por tipo (apenas pinos atualmente filtrados)
  const contagemPorTipo: Record<string, number> = {};
  pinos.forEach((p) => {
    const t = p.tipo_zeladoria ?? 'outros';
    contagemPorTipo[t] = (contagemPorTipo[t] ?? 0) + 1;
  });

  return (
    <PortalPage
      titulo="Mapa da zeladoria"
      subtitulo={`${estab?.nome ? estab.nome + ' · ' : ''}Demandas urbanas em tempo real (sigilosas e sem GPS não aparecem)`}
      icone={<FiMap size={26} />}
      voltarHref="/"
      largura="larga"
      acoes={
        <div className="flex items-center gap-2">
          <Link
            to="/mapa"
            className="inline-flex items-center gap-1.5 rounded-md bg-white/10 px-3 py-2 text-sm font-medium text-white ring-1 ring-white/20 transition hover:bg-white/20"
          >
            <FiMap size={15} /> Todas as demandas
          </Link>
          <Link
            to="/zeladoria"
            className="inline-flex items-center gap-1.5 rounded-md bg-white/10 px-3 py-2 text-sm font-medium text-white ring-1 ring-white/20 transition hover:bg-white/20"
          >
            <FiPlus size={15} /> Reportar problema
          </Link>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">
        {/* Sidebar de filtros */}
        <aside className="space-y-5">
          <SectionCard titulo="Filtros" icone={<FiFilter size={18} />}>
            <div className="mb-4">
              <label className="text-xs font-medium text-slate-700 mb-1 block">Tipo de demanda</label>
              <select
                value={filtroTipo}
                onChange={(e) => setFiltroTipo(e.target.value as TipoZeladoria | '')}
                className="w-full h-9 px-2 rounded border border-slate-300 bg-white text-sm"
              >
                <option value="">Todos</option>
                {TIPOS_ZELADORIA_LISTA.map((t) => (
                  <option key={t} value={t}>
                    {TIPO_ZELADORIA_LABEL[t]}
                    {contagemPorTipo[t] ? ` (${contagemPorTipo[t]})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={apenasAbertas}
                onChange={(e) => setApenasAbertas(e.target.checked)}
              />
              <span>Apenas não-resolvidas</span>
            </label>
          </SectionCard>

          <SectionCard titulo="Legenda" icone={<FiList size={18} />}>
            <ul className="space-y-1.5 text-xs">
              {TIPOS_ZELADORIA_LISTA.map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <span
                    className="w-3 h-3 rounded-full border-2 border-white shadow"
                    style={{ background: TIPO_ZELADORIA_COR[t] }}
                  />
                  <span className="text-slate-700">{TIPO_ZELADORIA_LABEL[t]}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-500 mt-3 pt-2 border-t border-slate-100">
              Pinos com opacidade reduzida representam demandas já resolvidas.
            </p>
          </SectionCard>

          <SectionCard>
            <div className="text-xs text-slate-600">
              <strong className="text-slate-900">{pinos.length}</strong> {pinos.length === 1 ? 'demanda' : 'demandas'} no mapa
            </div>
          </SectionCard>
        </aside>

        {/* Mapa */}
        <div>
          {carregando ? (
            <div className="flex justify-center items-center bg-white rounded-2xl border border-slate-200" style={{ height: '500px' }}>
              <Spinner tamanho="lg" />
            </div>
          ) : erro ? (
            <Alert variante="erro">{erro}</Alert>
          ) : pinos.length === 0 ? (
            <SectionCard>
              <div className="text-center py-12">
                <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
                  <FiMapPin className="text-slate-400" size={26} />
                </div>
                <p className="text-sm font-medium text-slate-700">
                  {filtroTipo || apenasAbertas
                    ? 'Nenhuma demanda com os filtros atuais.'
                    : 'Ainda não há demandas com geolocalização registradas.'}
                </p>
              </div>
            </SectionCard>
          ) : (
            <SectionCard semPadding corpoClassName="p-2">
              <MapaZeladoria pinos={pinos} altura="600px" />
            </SectionCard>
          )}
        </div>
      </div>
    </PortalPage>
  );
}
